import { FeatureFlag } from '@ps/web-core-enums';
import React, { useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import TickIcon from '@swf/core/assets/icons/tick.svg';
import type { InjectedWizardLoadingProps } from '@swf/core/hoc/withWizardLoading';
import withWizardLoading, { Status } from '@swf/core/hoc/withWizardLoading';
import useFeature from '@swf/core/hooks/useFeature';

import Button from '../../../../../Button/Button';

import {
  TXTCLI_Create_Account,
  TXTWCL_Continue,
  TXTWCL_SuccessX
} from './CreateAccountButton.messages.json';
import {
  buttonEnter,
  buttonEnterActive,
  buttonEnterDone,
  buttonExit,
  buttonExitActive,
  buttonExitDone,
  buttonGhost,
  container,
  loadingEnter,
  loadingEnterActive,
  loadingEnterDone,
  loadingExit,
  successEnter,
  successEnterDone
} from './CreateAccountButton.scss';
import Ghost from './components/Ghost/Ghost';
import Loading from './components/Loading/Loading';

type Props = InjectedWizardLoadingProps;

const buttonExitDuration = 200;
const buttonEnterDuration = 666.6666;
const loadingEnterDuration = 666.6666;
const tickStrokeDuration = 500;

const CreateAccountButton: React.FunctionComponent<Props> = ({ status }) => {
  const [buttonExited, setButtonExited] = useState(false);
  const [loadingExited, setLoadingExited] = useState(true);
  const blacklistCheck = useFeature(FeatureFlag.BlackListCheck);

  return (
    <>
      <CSSTransition
        classNames={{
          enter: buttonEnter,
          enterActive: buttonEnterActive,
          enterDone: buttonEnterDone,
          exit: buttonExit,
          exitActive: buttonExitActive,
          exitDone: buttonExitDone
        }}
        in={loadingExited && [Status.Failure, Status.Idle].includes(status)}
        timeout={{
          enter: buttonEnterDuration,
          exit: buttonExitDuration
        }}
        onEntered={() => setButtonExited(false)}
        onExited={() => setButtonExited(true)}
        unmountOnExit
      >
        <div className={container}>
          <Ghost className={buttonGhost} />
          <Button
            type="submit"
            translation={blacklistCheck ? TXTWCL_Continue : TXTCLI_Create_Account}
          />
        </div>
      </CSSTransition>
      <CSSTransition
        classNames={{
          enter: loadingEnter,
          enterActive: loadingEnterActive,
          enterDone: loadingEnterDone,
          exitActive: loadingExit
        }}
        in={buttonExited && status === Status.Loading}
        timeout={loadingEnterDuration}
        onEntered={() => setLoadingExited(false)}
        onExited={() => setLoadingExited(true)}
        unmountOnExit
      >
        <div className={container}>
          <Ghost className={buttonGhost}>{!loadingExited && <Loading />}</Ghost>
        </div>
      </CSSTransition>
      <CSSTransition
        classNames={{
          enter: successEnter,
          enterDone: successEnterDone
        }}
        in={status === Status.Success}
        timeout={tickStrokeDuration + loadingEnterDuration}
        mountOnEnter
        unmountOnExit
      >
        <div className={container}>
          <Ghost className={buttonGhost} label={TXTWCL_SuccessX}>
            <TickIcon />
          </Ghost>
        </div>
      </CSSTransition>
    </>
  );
};

export default withWizardLoading(CreateAccountButton);
